import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  styled,
  Fab,
  Stack,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/footer';
import { apigetPublic } from '../../service/api';
import DashboardFilterBar, { DashboardNoMatches } from '../../components/dashboard/DashboardFilterBar';
import useDashboardFilters from '../../components/dashboard/useDashboardFilters';

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
const loCount = (b) => b.loanOfficers?.length || 0;

const FILTER_CONFIG = {
  search: (b) => [b.name, b.slug, b.city, b.address, b.stateCode],
  filters: [
    { key: 'state', label: 'State', values: (b) => [String(b.stateCode || '').toUpperCase()] },
    {
      key: 'lo',
      label: 'Loan officer',
      values: (b) => (b.loanOfficers || []).map((lo) => `${lo.firstName} ${lo.lastName}`),
    },
  ],
  sorts: [
    { key: 'name', label: 'Name A–Z', compare: byName },
    {
      key: 'state',
      label: 'State',
      compare: (a, b) => String(a.stateCode || '').localeCompare(String(b.stateCode || '')) || byName(a, b),
    },
    { key: 'los', label: 'Most loan officers', compare: (a, b) => loCount(b) - loCount(a) || byName(a, b) },
  ],
  defaultSort: 'name',
};

const DashboardContainer = styled(Container)(() => ({
  paddingTop: '8px',
  paddingBottom: '50px',
}));

const BranchCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[3],
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[6],
  },
}));

const BranchLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
}));

const BranchImage = styled(CardMedia)(() => ({
  height: 180,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

export default function BranchDashboard() {
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBranches = async () => {
      const result = await apigetPublic('branches/search'); // adjust backend route if needed
      if (result?.status === 200 && Array.isArray(result.data?.result)) {
        setBranches(result.data.result);
      }
      setIsLoading(false);
    };
    fetchBranches();
  }, []);

  const { visible, total, active, clear, barProps } = useDashboardFilters(branches, FILTER_CONFIG);

  return (
    <>

      <Box
        sx={{
          backgroundImage: 'url(/assets/backgroundimageforherobanner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            minHeight: '100vh',
            position: 'relative',
          }}
        >
          <DashboardContainer>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              Branch Dashboard
            </Typography>

            <DashboardFilterBar
              noun="branches"
              {...barProps}
              shown={visible.length}
              total={total}
              active={active}
              onClear={clear}
            />

            {isLoading ? (
              <Typography align="center">Loading branches...</Typography>
            ) : branches.length > 0 && visible.length === 0 ? (
              <DashboardNoMatches noun="branches" onClear={clear} />
            ) : branches.length > 0 ? (
              <Grid container spacing={4}>
                {visible.map((branch) => (
                  <Grid item xs={12} sm={6} md={4} key={branch._id}>
                    <BranchLink to={`/dashboard/branches/${branch._id}`}>
                      <BranchCard>
                        
                      {/* Branch Image */}
  <BranchImage
    image={
      branch.branchImageUrl
        ? branch.branchImageUrl.startsWith("http")
          ? branch.branchImageUrl
          : `https://${branch.branchImageUrl.replace(/^https?:\/\//, "")}`
        : "/assets/images/no-image.jpg"
    }
  />

                        <CardContent>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            {branch.name}
                          </Typography>

                          <Typography variant="body1" color="text.secondary">
                            {branch.city}, {branch.stateCode}
                          </Typography>

                          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                            {branch.address}
                          </Typography>

                          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                            Loan Officers: {branch.loanOfficers?.length || 0}
                          </Typography>
                        </CardContent>

                       
                      </BranchCard>
                    </BranchLink>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography align="center" sx={{ mt: 4 }}>
                No branches found.
              </Typography>
            )}
          </DashboardContainer>

          {/* Floating Create Button */}
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => navigate('/dashboard/branches/create')}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#115293' },
            }}
          >
            <AddIcon />
          </Fab>
        </Box>
      </Box>

      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />
      <Footer />
    </>
  );
}
